import type { LogsInitConfiguration } from '@datadog/browser-logs'
import type { RumInitConfiguration } from '@datadog/browser-rum'

export const PLATFORMS = [
  'shopify',
  'sfcc',
  'buyquickly',
  'predictspring',
  // In-store retail (RP) tooling: booking-tool, check-in, check-out,
  // customer-profile, customer-search. Used inside stores, not customer-facing.
  'instore',
  'tailor-instructions',
] as const
export type Platform = (typeof PLATFORMS)[number]

export const ENVIRONMENTS = ['prd', 'tst', 'acc', 'dev'] as const
export type DatadogEnv = (typeof ENVIRONMENTS)[number]

/**
 * Tag keys that the package owns and writes to Datadog's global context.
 *
 * `additionalTags` callers cannot reuse these keys — `validateConfig` rejects
 * them so consumer-supplied values can never overwrite the validated config
 * and skew dashboards or alerts.
 */
export const RESERVED_TAG_KEYS = [
  'platform',
  'env',
  'country',
  'domain',
  'sub_service',
] as const
export type ReservedTagKey = (typeof RESERVED_TAG_KEYS)[number]

export type DatadogConfig = {
  service: string
  env: DatadogEnv
  version: string
  platform: Platform
  /**
   * Country code for regional filtering (e.g. `'US'`, `'NL'`). Optional —
   * apps that aren't tied to a single country (in-store tooling, multi-region
   * staff apps) should omit it instead of faking a value, which would skew
   * country-filtered dashboards.
   */
  country?: string
  /**
   * Optional store identifier used to filter Datadog dashboards/alerts by store.
   * Must be a non-negative integer when provided. Apps that aren't tied to a
   * specific store (e.g. a multi-tenant headless storefront) should omit it.
   */
  locationId?: number

  applicationId: string
  clientToken: string
  site?: string

  subService?: string

  sessionSampleRate?: number
  sessionReplaySampleRate?: number
  logsSampleRate?: number

  trackUserInteractions?: boolean
  trackResources?: boolean
  trackLongTasks?: boolean
  defaultPrivacyLevel?: 'mask-user-input' | 'mask' | 'allow'
  forwardErrorsToLogs?: boolean

  additionalTags?: Record<string, string>

  storeContextsAcrossPages?: boolean
  useSecureSessionCookie?: boolean

  /**
   * Optional beforeSend callback forwarded to `datadogRum.init()`. Use when
   * the caller needs to inspect or mutate individual RUM events before they
   * are sent — for example, to override `event.service` based on global
   * context set by an embedded app.
   */
  rumBeforeSend?: RumInitConfiguration['beforeSend']

  /**
   * Optional beforeSend callback forwarded to `datadogLogs.init()`. Same
   * purpose as `rumBeforeSend` but for log events.
   */
  logsBeforeSend?: NonNullable<LogsInitConfiguration['beforeSend']>
}
